import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import PageHeader from '../components/common/PageHeader';
import { FaUser, FaLock, FaEye, FaEyeSlash } from 'react-icons/fa';

const ClientLoginPage = () => {
  const [showPassword, setShowPassword] = React.useState(false);
  const [formData, setFormData] = React.useState({
    username: '',
    password: ''
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { label: 'Client Login' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Redirect to external auth URL
    window.location.href = 'https://web3.blockfirstcapital.com/';
  };

  return (
    <>
      <PageHeader 
        title="Client Login" 
        breadcrumbs={breadcrumbs}
        backgroundImage="/assets/person-office-analyzing-checking-finance-graphs.jpg"
      />
      
      <section className="py-16 md:py-24 bg-background">
        <div className="container-custom">
          <div className="max-w-md mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="bg-white rounded-2xl shadow-custom p-8"
            >
              <div className="text-center mb-8">
                <motion.div
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5 }}
                  className="bg-primary/10 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
                >
                  <FaUser className="w-8 h-8 text-primary" />
                </motion.div>
                <h2 className="text-3xl font-heading font-bold text-primary mb-2">Welcome Back</h2>
                <p className="text-gray-600">Please sign in to access your account</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                    Username
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaUser className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="username"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Enter your username"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaLock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className="block w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Enter your password"
                      required
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <FaEyeSlash className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      ) : (
                        <FaEye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input
                      id="remember-me"
                      name="remember-me"
                      type="checkbox"
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                    />
                    <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                      Remember me
                    </label>
                  </div>

                  <div className="text-sm">
                    <a href="#" className="font-medium text-primary hover:text-secondary transition-colors">
                      Forgot password?
                    </a>
                  </div>
                </div>

                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full btn btn-primary text-lg py-3 shadow-xl hover:shadow-primary/20 btn-3d"
                >
                  Sign In
                </motion.button>
              </form>

              <div className="mt-8 text-center">
                <p className="text-sm text-gray-600">
                  Don't have an account?{' '}
                  <a href="/contact-us" className="font-medium text-primary hover:text-secondary transition-colors">
                    Contact us to get started
                  </a>
                </p>
              </div>
            </motion.div>

            {/* Security Notice */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="mt-8 bg-gray-50 rounded-lg p-6"
            >
              <h3 className="text-lg font-semibold text-primary mb-3">Security Notice</h3>
              <ul className="text-sm text-gray-600 space-y-2">
                <li>• Your connection is encrypted and secure</li>
                <li>• Never share your login credentials with anyone</li>
                <li>• Always log out when finished with your session</li>
                <li>• Contact us immediately if you suspect unauthorized access</li>
              </ul>
            </motion.div>

            {/* Contact Support */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.5 }}
              className="mt-6 text-center"
            >
              <p className="text-sm text-gray-600 mb-3">Need assistance with your account?</p>
              <a 
                href="/contact-us" 
                className="inline-block btn btn-outline text-sm px-6 py-2 hover:bg-primary hover:text-white transition-all duration-300"
              >
                Contact Support
              </a>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ClientLoginPage; 